// src/SubstituteStudentsSkills.js
import React, { useEffect, useState } from 'react';
import API from './api';

const statusColors = {
  not_achieved: 'bg-red-200',
  emerging: 'bg-yellow-200',
  achieved: 'bg-green-200',
};

const DISC_COLORS = {
  EI: 'bg-blue-100 text-blue-800 border-blue-300',
  PI: 'bg-orange-100 text-orange-800 border-orange-300',
  ABA: 'bg-purple-100 text-purple-800 border-purple-300',
  OT: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  PT: 'bg-teal-100 text-teal-800 border-teal-300',
  ST: 'bg-green-100 text-green-800 border-green-300',
  default: 'bg-gray-100 text-gray-800 border-gray-300',
};

function SubstituteStudentsSkills() {
  const [students, setStudents] = useState([]);
  const [disciplinesByStudent, setDisciplinesByStudent] = useState({});
  const [skillsByStudentDiscipline, setSkillsByStudentDiscipline] = useState({});
  const [availableMonths, setAvailableMonths] = useState([]);

  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [selectedDisciplineId, setSelectedDisciplineId] = useState('');

  const [workingSkills, setWorkingSkills] = useState({});
  const [monthFilter, setMonthFilter] = useState('');

  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [selectedSkillName, setSelectedSkillName] = useState('');
  const [selectedStudentName, setSelectedStudentName] = useState('');

  const [noteInput, setNoteInput] = useState('');
  const [pendingUpdate, setPendingUpdate] = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const res = await API.get('/substitute-students-skills');
      const {
        students = [],
        disciplines_by_student = {},
        skills_by_student_discipline = {},
        available_months = [],
      } = res.data || {};

      setStudents(students);
      setDisciplinesByStudent(disciplines_by_student);
      setSkillsByStudentDiscipline(skills_by_student_discipline);
      setAvailableMonths(available_months || []);
      setWorkingSkills({});
    } catch (err) {
      console.error(err);
      alert('Failed to load substitute students skills');
    }
  };

  // Kad se promeni student, biramo prvu disciplinu
  useEffect(() => {
    if (!selectedStudentId) {
      setSelectedDisciplineId('');
      return;
    }
    const list = disciplinesByStudent[selectedStudentId] || [];
    if (!list.length) {
      setSelectedDisciplineId('');
      return;
    }
    setSelectedDisciplineId(prev => {
      if (prev && list.some(d => String(d.discipline_id) === String(prev))) {
        return prev;
      }
      return String(list[0].discipline_id);
    });
  }, [selectedStudentId, disciplinesByStudent]);

  const addWorkingSkill = (studentId, disciplineId, domainKey, skillId) => {
    if (!skillId) return;
    const key = `${studentId}_${disciplineId}_${domainKey}`;
    setWorkingSkills(prev => ({
      ...prev,
      [key]: [...(prev[key] || []), parseInt(skillId, 10)],
    }));
  };

  const openHistory = async (student_id, skill_id, skillName, studentName) => {
    try {
      const res = await API.get(`/students/${student_id}/skills/${skill_id}/history`);
      setHistoryData(res.data || []);
      setSelectedSkillName(skillName);
      setSelectedStudentName(studentName);
      setShowHistory(true);
    } catch (err) {
      console.error(err);
      alert('Failed to load history');
    }
  };

  const updateSkillWithNote = async () => {
    if (!pendingUpdate) return;
    try {
      await API.post(
        `/students/${pendingUpdate.student_id}/skills/${pendingUpdate.skill_id}/substitute`,
        {
          status: pendingUpdate.newStatus,
          is_baseline: pendingUpdate.is_baseline || false,
          note: pendingUpdate.note || noteInput,
          default_teacher_id: pendingUpdate.default_teacher_id,
        }
      );
      setShowNoteModal(false);
      setPendingUpdate(null);
      await fetchAll();
    } catch (err) {
      console.error(err);
      alert('Update failed');
    }
  };

  const renderStudentBlock = (student) => {
    const studentId = student.id;
    const studentName = `${student.first_name} ${student.last_name}`;
    const disciplines = disciplinesByStudent[studentId] || [];

    if (!selectedDisciplineId) {
      return (
        <div key={studentId} className="mb-8 border rounded p-4 shadow">
          <h3 className="font-semibold text-lg mb-2">{studentName}</h3>

          <div className="mb-4">
            <div className="text-sm font-semibold mb-1">Disciplines:</div>
            {disciplines.length ? (
              <div className="flex flex-wrap gap-2">
                {disciplines.map(d => {
                  const active = String(d.discipline_id) === String(selectedDisciplineId);
                  const baseClass =
                    DISC_COLORS[d.service_type] || DISC_COLORS.default;
                  return (
                    <button
                      key={d.discipline_id}
                      onClick={() =>
                        setSelectedDisciplineId(String(d.discipline_id))
                      }
                      className={`px-2 py-1 text-xs rounded-full border ${baseClass} ${
                        active ? 'ring-2 ring-offset-1 ring-blue-500 font-semibold' : ''
                      }`}
                    >
                      {d.service_type} – {d.name}
                      {d.is_primary ? ' (primary)' : ''}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="text-xs text-gray-500">
                No disciplines assigned for this student.
              </div>
            )}
          </div>

          <div className="text-sm text-gray-500">
            Select a discipline badge to start working with skills.
          </div>
        </div>
      );
    }

    const disciplineIdNum = parseInt(selectedDisciplineId, 10);
    const sdKey = `${studentId}_${disciplineIdNum}`;
    const allSkills = skillsByStudentDiscipline[sdKey] || [];

    if (!allSkills.length) {
      return (
        <div key={studentId} className="mb-8 border rounded p-4 shadow">
          <h3 className="font-semibold text-lg mb-2">{studentName}</h3>

          <div className="mb-4">
            <div className="text-sm font-semibold mb-1">Disciplines:</div>
            <div className="flex flex-wrap gap-2">
              {disciplines.map(d => {
                const active = String(d.discipline_id) === String(selectedDisciplineId);
                const baseClass =
                  DISC_COLORS[d.service_type] || DISC_COLORS.default;
                return (
                  <button
                    key={d.discipline_id}
                    onClick={() =>
                      setSelectedDisciplineId(String(d.discipline_id))
                    }
                    className={`px-2 py-1 text-xs rounded-full border ${baseClass} ${
                      active ? 'ring-2 ring-offset-1 ring-blue-500 font-semibold' : ''
                    }`}
                  >
                    {d.service_type} – {d.name}
                    {d.is_primary ? ' (primary)' : ''}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="text-sm text-gray-500">
            No skills defined yet for this discipline.
          </div>
        </div>
      );
    }

    // --- NOVO: grupisanje po discipline-specific domainu (discipline_domains) ---
    const groupedByDomain = {};
    allSkills.forEach(skill => {
      const domKey = skill.domain_id || skill.domain || 'other';
      if (!groupedByDomain[domKey]) {
        groupedByDomain[domKey] = {
          domainKey: domKey,
          domainId: skill.domain_id || null,
          domainCode: skill.domain_code || null,
          domainName:
            skill.domain_name ||
            (skill.domain ? skill.domain.replace('_', ' ') : 'Other'),
          domainDescription: skill.domain_description || '',
          skills: [],
        };
      }
      groupedByDomain[domKey].skills.push(skill);
    });

    return (
      <div key={studentId} className="mb-8 border rounded p-4 shadow">
        <h3 className="font-semibold text-lg mb-2">{studentName}</h3>

        {/* Discipline badge-ovi */}
        <div className="mb-4">
          <div className="text-sm font-semibold mb-1">Disciplines:</div>
          <div className="flex flex-wrap gap-2">
            {disciplines.map(d => {
              const active = String(d.discipline_id) === String(selectedDisciplineId);
              const baseClass =
                DISC_COLORS[d.service_type] || DISC_COLORS.default;
              return (
                <button
                  key={d.discipline_id}
                  onClick={() =>
                    setSelectedDisciplineId(String(d.discipline_id))
                  }
                  className={`px-2 py-1 text-xs rounded-full border ${baseClass} ${
                    active ? 'ring-2 ring-offset-1 ring-blue-500 font-semibold' : ''
                  }`}
                >
                  {d.service_type} – {d.name}
                  {d.is_primary ? ' (primary)' : ''}
                </button>
              );
            })}
          </div>
        </div>

        {Object.values(groupedByDomain).map(group => {
          const {
            domainKey,
            domainId,
            domainCode,
            domainName,
            domainDescription,
            skills: domainSkills,
          } = group;

          const baselineSkill = domainSkills.find(s => s.is_baseline);
          const hasBaseline = !!baselineSkill;

          const postBaselineSkills = domainSkills.filter(
            s => baselineSkill && s.position_order > baselineSkill.position_order
          );

          const wkDomainKey = domainId || domainKey;
          const wkKey = `${studentId}_${disciplineIdNum}_${wkDomainKey}`;
          const addedSkills = workingSkills[wkKey] || [];

          const filteredSkills = domainSkills.filter(skill => {
            if (!monthFilter) return true;
            return skill.assessed_at?.startsWith(monthFilter);
          });

          const activeSkills = filteredSkills.filter(
            s =>
              baselineSkill &&
              s.position_order > baselineSkill.position_order &&
              ['not_achieved', 'emerging', 'achieved'].includes(s.status)
          );

          const currentWorking = [
            ...activeSkills,
            ...postBaselineSkills.filter(
              s =>
                addedSkills.includes(s.id) &&
                !activeSkills.some(a => a.id === s.id)
            ),
          ];

          const headerLabel = domainName || 'Other';

          const baselineSelectId = `baseline-sub-${studentId}-${disciplineIdNum}-${wkDomainKey}`;
          const addSkillSelectId = `add-skill-sub-${studentId}_${disciplineIdNum}_${wkDomainKey}`;

          return (
            <div key={wkDomainKey} className="mb-6">
              <div className="font-semibold uppercase mb-1">
                {headerLabel}
              </div>
              {domainDescription && (
                <div className="text-xs text-gray-500 mb-2">
                  {domainDescription}
                </div>
              )}

              {/* Set Baseline blok */}
              {!hasBaseline ? (
                <div className="flex flex-col items-stretch gap-2 w-full max-w-md mx-auto mb-3">
                  <select
                    className="border rounded px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                    defaultValue=""
                    id={baselineSelectId}
                  >
                    <option value="">Select Baseline</option>
                    {domainSkills.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.age_range} | {s.code} – {s.name}
                      </option>
                    ))}
                  </select>
                  <button
                    className="px-3 py-2 bg-blue-500 text-white rounded text-sm w-full"
                    onClick={async () => {
                      const select = document.getElementById(baselineSelectId);
                      const selectedId = parseInt(select.value, 10);
                      if (selectedId) {
                        try {
                          await API.post(
                            `/students/${studentId}/skills/${selectedId}/substitute`,
                            {
                              status: 'not_achieved',
                              is_baseline: true,
                              note: '',
                              default_teacher_id: student.default_teacher_id,
                            }
                          );
                          await fetchAll();
                        } catch (err) {
                          console.error(err);
                          alert('Baseline set failed');
                        }
                      }
                    }}
                  >
                    Set Baseline
                  </button>
                </div>
              ) : (
                <div className="mb-3">
                  <span className="text-green-600 font-semibold">
                    Baseline set: {baselineSkill.code} – {baselineSkill.name}
                  </span>
                </div>
              )}

              {/* Add Skill blok (samo za najnoviji mesec) */}
              {hasBaseline &&
                (!monthFilter || monthFilter === availableMonths[0]) && (
                  <div className="flex flex-col items-stretch gap-2 w-full max-w-md mx-auto mb-3">
                    <select
                      className="border rounded px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-400"
                      id={addSkillSelectId}
                    >
                      <option value="">Add skill...</option>
                      {postBaselineSkills.map(s => (
                        <option key={s.id} value={s.id}>
                          {s.age_range} | {s.code} – {s.name}
                        </option>
                      ))}
                    </select>
                    <button
                      className="px-3 py-2 bg-indigo-500 text-white rounded text-sm w-full"
                      onClick={() => {
                        const select = document.getElementById(addSkillSelectId);
                        const selectedId = parseInt(select.value, 10);
                        addWorkingSkill(studentId, disciplineIdNum, wkDomainKey, selectedId);
                        select.value = '';
                      }}
                    >
                      Add Skill
                    </button>
                  </div>
                )}

              {/* Tabela */}
              {hasBaseline && currentWorking.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full table-auto text-sm border mt-2">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="p-2">Code</th>
                        <th className="p-2">Skill</th>
                        <th className="p-2">Status</th>
                        <th className="p-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentWorking.map(skill => {
                        const isAchieved = skill?.status === 'achieved';
                        return (
                          <tr key={skill.id} className="border-b">
                            <td className="p-2 text-center">
                              {skill.code}
                              <div className="text-xs text-gray-500">
                                {skill.age_range}
                              </div>
                            </td>
                            <td className="p-2">{skill.name}</td>
                            <td
                              className={`p-2 ${
                                statusColors[skill.status] || ''
                              }`}
                            >
                              {skill.status || '–'}
                              {skill.substitute_teacher_id && (
                                <span className="text-xs text-gray-600 ml-1">
                                  (substitute)
                                </span>
                              )}
                            </td>
                            <td className="p-2 space-x-1">
                              {!isAchieved &&
                                ['not_achieved', 'emerging', 'achieved'].map(
                                  st => (
                                    <button
                                      key={st}
                                      className={`px-2 py-1 text-xs rounded ${
                                        skill.status === st
                                          ? 'bg-gray-400 text-white'
                                          : 'bg-blue-200'
                                      }`}
                                      onClick={() => {
                                        setPendingUpdate({
                                          student_id: studentId,
                                          skill_id: skill.id,
                                          newStatus: st,
                                          default_teacher_id:
                                            student.default_teacher_id,
                                        });
                                        setNoteInput('');
                                        setShowNoteModal(true);
                                      }}
                                    >
                                      {st.replace('_', ' ')}
                                    </button>
                                  )
                                )}
                              <button
                                className="ml-2 px-2 py-1 text-xs rounded border border-gray-400 text-gray-700"
                                onClick={() =>
                                  openHistory(
                                    studentId,
                                    skill.id,
                                    skill.name,
                                    studentName
                                  )
                                }
                              >
                                History
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const selectedStudent = students.find(
    s => String(s.id) === String(selectedStudentId)
  );

  return (
    <div className="p-1">
      <h2 className="text-lg font-bold mb-4">Substitute Students Skills</h2>

      <div className="flex flex-wrap gap-4 items-center mb-4">
        <div>
          <label className="mr-2 font-semibold">Select student:</label>
          <select
            className="border px-2 py-1 rounded text-sm"
            value={selectedStudentId}
            onChange={e => {
              setSelectedStudentId(e.target.value);
              setMonthFilter('');
            }}
          >
            <option value="">-- Choose --</option>
			{[...students]
			  .sort((a, b) => {
			    const al = (a.last_name || '').toLowerCase();
			    const bl = (b.last_name || '').toLowerCase();
			    if (al !== bl) return al.localeCompare(bl);
			    const af = (a.first_name || '').toLowerCase();
			    const bf = (b.first_name || '').toLowerCase();
			    return af.localeCompare(bf);
			  })
			  .map(s => (
			    <option key={s.id} value={s.id}>
			      {`${s.last_name || ''}${s.last_name ? ', ' : ''}${s.first_name || ''}`.trim()}
			    </option>
			  ))}
            
          </select>
        </div>

        {selectedStudentId && availableMonths.length > 0 && (
          <div>
            <label className="mr-2 font-semibold">Filter by month:</label>
            <select
              className="border px-2 py-1 rounded text-sm"
              value={monthFilter}
              onChange={e => setMonthFilter(e.target.value)}
            >
              <option value="">All</option>
              {availableMonths.map(m => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {selectedStudent ? (
        renderStudentBlock(selectedStudent)
      ) : (
        <div className="text-sm text-gray-500">
          Please select a student to view and edit skills as substitute.
        </div>
      )}

      {/* Note modal */}
      {showNoteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded w-full max-w-md">
            <h2 className="text-lg font-bold mb-4">Add Note</h2>
            <textarea
              rows={4}
              className="w-full border rounded p-2 text-sm"
              placeholder="Optional notes..."
              value={noteInput}
              onChange={e => setNoteInput(e.target.value)}
            />
            <div className="mt-4 flex justify-end space-x-2">
              <button
                onClick={() => setShowNoteModal(false)}
                className="px-4 py-1 bg-gray-300 rounded"
              >
                Cancel
              </button>
              <button
                onClick={updateSkillWithNote}
                className="px-4 py-1 bg-blue-500 text-white rounded"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History modal */}
      {showHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-3 sm:p-6 rounded w-[98%] max-w-xl max-h-[90vh] overflow-y-auto shadow-lg">
            <h2 className="text-base sm:text-lg font-bold mb-3 sm:mb-4">
              History: {selectedSkillName} for {selectedStudentName}
            </h2>
            <div className="overflow-x-auto mb-2">
              <table className="w-full text-xs sm:text-sm mb-3">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="p-2">Date</th>
                    <th className="p-2">From</th>
                    <th className="p-2">To</th>
                    <th className="p-2">Note</th>
                    <th className="p-2">Evaluator</th>
                  </tr>
                </thead>
                <tbody>
                  {historyData.map((entry, idx) => (
                    <tr key={idx} className="border-b">
                      <td className="p-2 whitespace-nowrap">
                        {new Date(entry.changed_at).toLocaleString()}
                      </td>
                      <td className="p-2">{entry.previous_status || '–'}</td>
                      <td className="p-2">{entry.new_status}</td>
                      <td className="p-2">{entry.note || ''}</td>
                      <td className="p-2">
                        {entry.substitute_name || entry.teacher_name || '–'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setShowHistory(false)}
                className="px-4 py-2 bg-gray-200 rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SubstituteStudentsSkills;




